import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Flex,
  Input,
  PrimaryButton,
  SecondaryButton,
  Spacing,
  Text,
} from '@enechain/ecloud-designsystem'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import {
  EditIconButton,
  MasterDataTable,
} from '~/components/common/MasterDataTable'
import { FilterSection, FilterToolbar } from '~/components/common/filters'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { ContractorFormModal } from '~/pages/CustomerInfo/components/ContractorFormModal'
import { type SaveResult } from '~/pages/CustomerInfo/components/CustomerFormModal'
import { buildContractorColumns } from '~/pages/CustomerInfo/components/contractorColumns'
import {
  useDeleteContractor,
  useListContractors,
  useSaveContractor,
} from '~/pages/CustomerInfo/hooks/useCustomerInfoQueries'
import { resolveContractorError } from '~/pages/CustomerInfo/models/resolveSupplyPointError'
import {
  ContractorError,
  SupplyPointError,
} from '~/pages/CustomerInfo/models/supplyPointError'
import type {
  ContractorInput,
  ContractorRow,
} from '~/pages/CustomerInfo/models/types'

type ModalState =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; row: ContractorRow }
  | { kind: 'delete'; row: ContractorRow }

/**
 * 契約者タブ。 契約者 = 小売供給契約を結び、 請求を受ける法人。 需要家は必ず 1 つの契約者に属する。
 * 列: 契約者名 / 需要家数 / 供給地点数 / 請求書の送付先 / 最終更新日時 / 最終更新者。
 * 検索は契約者名・需要家名の部分一致。 削除は編集モーダルの Footer 左端 → 確認ダイアログ
 * (配下に需要家がある間は押せず、 理由は title に出す)。
 */
export const ContractorTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { data, isLoading, isError, refetch } = useListContractors()
  const showErrorToast = useErrorToast()
  const save = useSaveContractor()
  const remove = useDeleteContractor()
  const [searchText, setSearchText] = useState('')
  const [modalState, setModalState] = useState<ModalState>({ kind: 'none' })

  const rows = useMemo(() => {
    const keyword = searchText.trim().toLowerCase()
    return (data ?? []).filter(
      (row) =>
        keyword === '' ||
        row.contractor.name.toLowerCase().includes(keyword) ||
        row.customerNames.toLowerCase().includes(keyword),
    )
  }, [data, searchText])

  const closeModal = (): void => setModalState({ kind: 'none' })

  const handleSave = async (input: ContractorInput): Promise<SaveResult> => {
    const editing = modalState.kind === 'edit' ? modalState.row : undefined
    try {
      // 編集は開いた時点の行 (modalState に固定) の version を楽観ロックの token として送る。
      await save.mutateAsync({
        id: editing?.contractor.id,
        input,
        expectedUpdatedAt: editing?.contractor.version,
      })
      showSuccessToast({
        title:
          editing === undefined
            ? t('page:customerInfo.contractors.toast.created')
            : t('page:customerInfo.contractors.toast.updated'),
      })
      closeModal()
      return undefined
    } catch (err) {
      if (err instanceof SupplyPointError && err.reason === 'CONFLICT') {
        return { conflict: true }
      }
      return { error: resolveContractorError(err, t) }
    }
  }

  const handleDelete = (row: ContractorRow): void => {
    void (async () => {
      try {
        await remove.mutateAsync(row.contractor.id)
        showSuccessToast({
          title: t('page:customerInfo.contractors.toast.deleted'),
        })
      } catch (err) {
        // 配下に需要家がある間は削除ボタンが押せない。 確認中に他で紐付いた (競合) 時は最新の一覧を取り直す。
        // 通信・DB の障害も含め、 削除できなかったことを知らせる。
        showErrorToast({ title: resolveContractorError(err, t) })
        if (err instanceof ContractorError && err.reason === 'HAS_CUSTOMERS') {
          void refetch()
        }
      }
      closeModal()
    })()
  }

  const resolveMemberName = useMemberDisplayName()
  const columns = buildContractorColumns(t, resolveMemberName)

  const editing = modalState.kind === 'edit' ? modalState.row : undefined

  return (
    <Flex flexDirection="column" gap={Spacing[3].value}>
      <FilterToolbar>
        <FilterSection label={t('page:customerInfo.list.filter.search')}>
          <Input
            maxWidth="280px"
            aria-label={t('page:customerInfo.list.filter.search')}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('page:customerInfo.list.filter.searchPlaceholder')}
            type="search"
            value={searchText}
          />
        </FilterSection>
      </FilterToolbar>

      <Flex alignItems="center" justifyContent="space-between">
        <Text color="Text.midEmphasis" fontSize="md">
          {t('page:customerInfo.common.count', { count: rows.length })}
        </Text>
        <Flex alignItems="center" gap={2}>
          {isError && (
            <SecondaryButton onClick={() => void refetch()} size="sm">
              {t('page:customerInfo.common.retry')}
            </SecondaryButton>
          )}
          <PrimaryButton
            onClick={() => setModalState({ kind: 'create' })}
            size="sm"
            title={t('page:customerInfo.contractors.modal.createTitle')}
          >
            {t('page:customerInfo.action.add')}
          </PrimaryButton>
        </Flex>
      </Flex>

      <MasterDataTable
        columns={columns}
        emptyLabel={t('page:customerInfo.contractors.table.empty')}
        errorLabel={
          isError ? t('page:customerInfo.common.loadError') : undefined
        }
        getRowKey={(row) => row.contractor.id}
        isLoading={isLoading}
        onRowClick={(row) => setModalState({ kind: 'edit', row })}
        renderRowActions={(row) => (
          <EditIconButton
            onClick={() => setModalState({ kind: 'edit', row })}
          />
        )}
        rows={rows}
      />

      {(modalState.kind === 'create' || modalState.kind === 'edit') && (
        <ContractorFormModal
          contractor={editing?.contractor}
          deleteBlockReason={
            editing !== undefined && editing.customerCount > 0
              ? t('page:customerInfo.contractors.deleteBlocked', {
                  count: editing.customerCount,
                })
              : undefined
          }
          isPending={save.isPending}
          onClose={closeModal}
          onDelete={
            editing === undefined
              ? undefined
              : () => setModalState({ kind: 'delete', row: editing })
          }
          onSave={handleSave}
        />
      )}

      {modalState.kind === 'delete' && (
        <ConfirmDialog
          body={t('page:customerInfo.contractors.delete.body', {
            name: modalState.row.contractor.name,
          })}
          confirmLabel={t('common:action.delete')}
          destructive
          isLoading={remove.isPending}
          isOpen
          onCancel={closeModal}
          onConfirm={() => handleDelete(modalState.row)}
          title={t('page:customerInfo.contractors.delete.title')}
        />
      )}
    </Flex>
  )
}
